import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Sparkles, Wallet, Megaphone, Clock, CheckCircle2, XCircle, Pause, AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatError, formatPaise } from '@/lib/api';
import { DELETABLE_STATUSES, DRAFT_STATUSES, statusOf } from '@/lib/campaign';
import Pagination from '@/components/Pagination';
import { changeTitle } from '@/components/OptimizerFeed';

const STATUS_ICONS = {
  generating: Sparkles, ai_question: AlertTriangle, rejected: XCircle, live: CheckCircle2,
  paused: Pause, ended: CheckCircle2, cancelled: XCircle,
};
const PAGE_SIZE = 10;
// The optimizer's latest news for a live ad: a warning or a version to approve first, else its last change.
function OptimizerLine({ campaign }) {
  if (campaign.poor_warning) {
    return <p className="text-xs font-medium mt-1 text-amber-700 dark:text-amber-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" aria-hidden="true" /> Not getting results yet. Tap to see what to do.</p>;
  }
  if (campaign.optimizer_proposal) {
    return <p className="text-xs font-medium mt-1 text-brand-strong flex items-center gap-1"><Sparkles className="w-3 h-3" aria-hidden="true" /> A fresher version is ready. Tap to review.</p>;
  }
  const last = [...(campaign.optimizer_log || [])].reverse().find(e => e.outcome === 'done');
  return last ? <p className="text-xs text-muted-foreground mt-1">Optimizer: {changeTitle(last)}</p> : null;
}
const applied = (campaign) => (campaign.meta_suggestions || []).filter(s => s.decision === 'applied').length;
const TABS = [['all', 'All'], ['running', 'Running'], ['drafts', 'Drafts'], ['finished', 'Finished']];
const EMPTY_TEXT = { running: 'No ads running right now.', drafts: 'No drafts.', finished: 'No finished ads yet.' };

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, running: 0, drafts: 0, finished: 0 });
  const [group, setGroup] = useState('all');
  const [page, setPage] = useState(1);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [toDelete, setToDelete] = useState(null); // the ad waiting for the customer to confirm deleting it
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get('/api/wallet/balance').then(({ data }) => setBalance(data.balance_paise)).catch(() => {});
  }, []);

  useEffect(() => {
    let stale = false;
    api.get('/api/campaigns', { params: { group, page, page_size: PAGE_SIZE } })
      .then(({ data }) => {
        if (stale) return;
        setCampaigns(data.items); setTotal(data.total); setCounts(data.counts);
      })
      .catch(() => {})
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [group, page, reload]);

  const pickTab = (g) => { setGroup(g); setPage(1); };

  const deleteAd = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/campaigns/${toDelete.id || toDelete._id}`);
      toast.success('Ad deleted');
      // Deleting the only ad on a later page moves back a page.
      if (campaigns.length === 1 && page > 1) setPage(p => p - 1); else setReload(n => n + 1);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
    setDeleting(false);
    setToDelete(null);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="flex gap-1"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div></div>;

  return (
    <div data-testid="dashboard-page">
      {/* Welcome */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-heading mb-1">
          Welcome back{user?.name ? `, ${user.name}` : ''}
        </h1>
        <p className="text-muted-foreground text-sm">Here's what's happening with your ads.</p>
      </motion.div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <motion.button type="button" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="neo-card p-5 text-left hover-lift" onClick={() => navigate('/wallet')} data-testid="wallet-balance-display">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-9 h-9 rounded-xl bg-money-soft border-2 border-ink flex items-center justify-center">
              <Wallet className="w-4 h-4 text-money" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold">Wallet balance</span>
          </div>
          <p className="text-3xl font-extrabold text-money font-heading tracking-tight">{formatPaise(balance)}</p>
        </motion.button>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="neo-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-9 h-9 rounded-xl tag-butter border-2 border-ink flex items-center justify-center">
              <Megaphone className="w-4 h-4 text-[#141414]" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold">All ads</span>
          </div>
          <p className="text-3xl font-extrabold font-heading tracking-tight">{counts.all}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="neo-card p-5">
          <div className="flex items-center gap-3 mb-3">
            <span className="w-9 h-9 rounded-xl tag-mint border-2 border-ink flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4 text-[#141414]" aria-hidden="true" />
            </span>
            <span className="text-sm font-semibold">Running now</span>
          </div>
          <p className="text-3xl font-extrabold font-heading tracking-tight">
            {counts.running}
          </p>
        </motion.div>
      </div>

      {/* Create Ad CTA */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="mb-8">
        <Button size="lg" className="gradient-bg h-14 px-7 text-base"
          onClick={() => navigate('/create-ad')} data-testid="create-ad-button">
          <Plus className="w-5 h-5" /> Make a new ad
        </Button>
      </motion.div>

      {/* Campaigns List */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-extrabold font-heading">Your ads</h2>
          {counts.all > 0 && (
            <div className="flex gap-1.5 overflow-x-auto" role="tablist" aria-label="Filter your ads">
              {TABS.map(([key, label]) => (
                <button key={key} type="button" role="tab" aria-selected={group === key} onClick={() => pickTab(key)}
                  className={`shrink-0 h-9 px-3 rounded-full border-2 text-sm font-semibold transition-colors ${group === key ? 'border-ink bg-butter shadow-neo-sm' : 'border-ink/20 bg-card hover:border-ink'}`}
                  data-testid={`tab-${key}`}>
                  {label} <span className="text-muted-foreground font-medium">{counts[key]}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {counts.all > 0 && campaigns.length === 0 ? (
          <div className="text-center py-10 rounded-2xl bg-card border-2 border-dashed border-ink/40 text-sm text-muted-foreground">
            {EMPTY_TEXT[group] || 'Nothing here.'}
          </div>
        ) : campaigns.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="text-center py-14 px-4 rounded-2xl bg-card border-2 border-dashed border-ink/40">
            <span className="w-14 h-14 rounded-2xl tag-butter border-2 border-ink mx-auto mb-4 flex items-center justify-center">
              <Megaphone className="w-6 h-6 text-[#141414]" aria-hidden="true" />
            </span>
            <p className="font-heading font-extrabold text-xl mb-1">No ads yet</p>
            <p className="text-sm text-muted-foreground mb-5">Tell us about your business and we&apos;ll set up your first ad.</p>
            <Button className="gradient-bg !font-extrabold text-base h-11 px-6" onClick={() => navigate('/create-ad')}>
              <Plus className="w-4 h-4" /> Make my first ad
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {campaigns.map((campaign, i) => {
              const status = statusOf(campaign.status);
              const StatusIcon = STATUS_ICONS[campaign.status] || Clock;
              return (
                <motion.div key={campaign.id || campaign._id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="p-4 rounded-2xl bg-card border-2 border-ink hover-lift cursor-pointer group"
                  onClick={() => navigate(DRAFT_STATUSES.includes(campaign.status)
                    ? `/create-ad/${campaign.id || campaign._id}`
                    : `/campaigns/${campaign.id || campaign._id}`)}
                  data-testid={`campaign-card-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <h3 className="text-base font-semibold truncate">
                          {campaign.brief?.promotion || campaign.brief?.goal || 'Untitled Campaign'}
                        </h3>
                        <Badge variant="outline" className={`shrink-0 text-[10px] px-2 py-0.5 ${status.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" /> {status.label}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        {campaign.brief?.goal && <span>Goal: {campaign.brief.goal}</span>}
                        {campaign.budget_paise > 0 && <span>Budget: {campaign.duration_days > 0 ? `${formatPaise(Math.round(campaign.budget_paise / campaign.duration_days))}/day` : formatPaise(campaign.budget_paise)}</span>}
                        {campaign.duration_days > 0 && <span>{campaign.duration_days} days</span>}
                      </div>
                      {campaign.status === 'live' && <OptimizerLine campaign={campaign} />}
                      {campaign.status === 'live' && applied(campaign) > 0 && (
                        <p className="text-xs text-money font-medium mt-1">
                          {applied(campaign)} Meta suggestion{applied(campaign) === 1 ? '' : 's'} applied
                        </p>
                      )}
                    </div>
                    {DELETABLE_STATUSES.includes(campaign.status) && (
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 shrink-0 text-red-700 dark:text-red-400 hover:bg-red-500/10"
                        onClick={(e) => { e.stopPropagation(); setToDelete(campaign); }}
                        aria-label="Delete this ad" data-testid={`delete-campaign-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
            <Pagination page={page} pageSize={PAGE_SIZE} total={total} label="ads"
              onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          </div>
        )}
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(open) => { if (!open && !deleting) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this ad?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{toDelete?.brief?.promotion || 'This ad'}&rdquo; will be removed from your dashboard. This can&apos;t be undone.
              {toDelete && !DRAFT_STATUSES.includes(toDelete.status) && ' Any unspent money from it still comes back to your wallet.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Keep it</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" disabled={deleting}
              onClick={(e) => { e.preventDefault(); deleteAd(); }} data-testid="confirm-delete-campaign">
              {deleting ? 'Deleting...' : 'Delete ad'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
